import { Injectable } from '@angular/core';
import { registerPlugin, CapacitorHttp } from '@capacitor/core';
import { BackgroundGeolocationPlugin } from '@capacitor-community/background-geolocation';
import { Geolocation } from '@capacitor/geolocation';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TripsService } from './trips.service';
import { SocketService } from './socket.service';
import { TokenService } from './token.service';
import { CityService } from './city.service';

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');
const OFFLINE_QUEUE_KEY = 'syncc_location_queue';
const REACHED_CP_KEY = 'syncc_reached_checkpoints';
const CP_QUEUE_KEY = 'syncc_checkpoint_queue';

interface QueuedLocation { tripId: number; lineId: number; lat: number; lng: number; ts: number; }
interface QueuedCheckpoint { tripId: number; checkpointId: number; visitedAt: string; }

export interface Checkpoint {
    id: number;
    name: string;
    lat: number;
    lng: number;
}

@Injectable({
    providedIn: 'root'
})
export class LocationService {
    private watcherId: string | null = null;
    private tripId: number | null = null;
    private vehicleId: number | null = null;
    private lineId: number | null = null;
    private checkpoints: Checkpoint[] = [];
    // id -> ISO timestamp of when the driver actually passed the checkpoint. The time must
    // outlive the sync attempt: if the POST fails offline, this is the only record of it, and
    // it is what /finish reconciles from. A bare Set would lose it and the server would have
    // to stamp every unsynced checkpoint with the finish time.
    private reachedCheckpoints: Map<number, string> = new Map();
    private tripFinishFired = false;
    private _isTripActive = false;
    private lastPingTime = 0;
    private offlineQueue: QueuedLocation[] = [];
    private checkpointQueue: QueuedCheckpoint[] = [];
    private lastPosition: [number, number] | null = null;
    private _isInForeground = true;

    private currentLocationSub = new BehaviorSubject<[number, number] | null>(null);
    public currentLocation$ = this.currentLocationSub.asObservable();

    private checkpointReachedSub = new BehaviorSubject<Checkpoint | null>(null);
    public checkpointReached$ = this.checkpointReachedSub.asObservable();

    private tripCompletedSub = new BehaviorSubject<boolean>(false);
    public tripCompleted$ = this.tripCompletedSub.asObservable();

    private tripReadyToFinishSub = new BehaviorSubject<{ ready: boolean, arrivalTime?: Date } | null>(null);
    public tripReadyToFinish$ = this.tripReadyToFinishSub.asObservable();

    constructor(
        private tripsService: TripsService,
        private socketService: SocketService,
        private tokenService: TokenService,
        private cityService: CityService,
    ) {
        // Track foreground/background state via Capacitor lifecycle events.
        // When the screen is locked the WebSocket may still appear "connected" for
        // 30–60 s (awaiting ping timeout), but we must use HTTP push immediately.
        document.addEventListener('pause', () => { this._isInForeground = false; }, false);
        document.addEventListener('resume', () => { this._isInForeground = true; }, false);

        // Flush queued locations and checkpoints whenever the socket reconnects
        this.socketService.on('connect', () => {
            void this.flushOfflineQueue();
            void this.flushCheckpointQueue();
        });
    }

    /**
     * Getter for trip activity status to inform permission logic.
     */
    get isTripActive(): boolean {
        return this._isTripActive;
    }

    /**
     * Checks if the BackgroundGeolocation plugin is actually available on this platform.
     */
    private async isPluginAvailable(): Promise<boolean> {
        try {
            // Testing if the plugin responds to a basic call
            await Geolocation.checkPermissions();
            return true;
        } catch (e: any) {
            if (e.message?.includes('not implemented')) {
                console.error('[LocationService] BackgroundGeolocation plugin NOT implemented on this platform.');
                return false;
            }
            // If it's just a permission error or similar, the plugin exists
            return true;
        }
    }

    /**
     * Starts tracking location and checking for checkpoint proximity.
     * This uses @capacitor-community/background-geolocation for true background support on iOS.
     */
    async startTracking(tripId: number, vehicleId: number, lineId: number, checkpoints: any[], alreadyReachedIds: number[] = []) {
        if (!await this.isPluginAvailable()) {
            console.warn('[LocationService] Tracking aborted: Plugin not available.');
            return;
        }

        this.tripId = tripId;
        this.vehicleId = vehicleId;
        this.lineId = lineId;
        // Backend stores actualCheckpoints as [{id, reachedAt}] objects; keep both so
        // has(cp.id) still works and already-cleared checkpoints aren't re-triggered.
        this.reachedCheckpoints = new Map();
        for (const item of (alreadyReachedIds as any[])) {
            const id = typeof item === 'object' && item !== null ? item.id : item;
            if (typeof id !== 'number' || isNaN(id)) continue;
            const at = typeof item === 'object' && item !== null ? item.reachedAt : undefined;
            this.reachedCheckpoints.set(id, at ? new Date(at).toISOString() : new Date().toISOString());
        }
        // Merge anything captured offline that never reached the server.
        await this.loadPersistedCheckpoints();
        this.tripFinishFired = false;
        this._isTripActive = true;
        this.lastPingTime = 0;
        this.lastPosition = null;

        // Parse checkpoints into a clean format with confirmed coordinates
        this.checkpoints = checkpoints.map(cp => {
            const lat = parseFloat(cp.lat ?? cp.latitude ?? cp.location?.lat ?? cp.location?.coordinates?.[1]);
            const lng = parseFloat(cp.lng ?? cp.longitude ?? cp.location?.lng ?? cp.location?.coordinates?.[0]);
            return {
                id: cp.id,
                name: cp.name || `Punto ${cp.id}`,
                lat,
                lng
            };
        }).filter(cp => !isNaN(cp.lat) && !isNaN(cp.lng));

        console.log(`[LocationService] Iniciando seguimiento en segundo plano para el viaje ${tripId}. Puntos de Control:`, this.checkpoints.length);

        if (this.watcherId) {
            await BackgroundGeolocation.removeWatcher({ id: this.watcherId });
        }

        this.watcherId = await BackgroundGeolocation.addWatcher(
            {
                backgroundTitle: "Viaje en curso",
                backgroundMessage: "Tu ubicación se está compartiendo con el sindicato.",
                requestPermissions: true,
                stale: true,
                distanceFilter: 0,
            },
            (location, err) => {
                if (err) {
                    console.error('[LocationService] background-geolocation error:', err);
                    return;
                }
                if (location) {
                    this.handleLocationUpdate(location);
                }
            }
        );

        // Force Immediate Start-up Update
        // Grabbing current position instantly so the bus appears on the map the second the trip begins.
        try {
            const current = await this.getCurrentPosition();
            if (current) {
                console.log('[LocationService] Immediate start-up location captured');
                this.handleLocationUpdate(current);
            }
        } catch (e) {
            console.warn('[LocationService] Initial position grab failed, waiting for watcher...', e);
        }
    }

    private async getCurrentPosition(): Promise<any> {
        try {
            const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
            return { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy };
        } catch (e) {
            console.warn('[LocationService] getCurrentPosition failed:', e);
            return null;
        }
    }

    async stopTracking() {
        this._isTripActive = false;

        if (this.watcherId) {
            await BackgroundGeolocation.removeWatcher({ id: this.watcherId });
            this.watcherId = null;
        }
        this.tripId = null;
        this.vehicleId = null;
        this.lineId = null;
        this.checkpoints = [];
        this.reachedCheckpoints.clear();
        this.checkpointQueue = [];
        void Preferences.remove({ key: REACHED_CP_KEY });
        void Preferences.remove({ key: CP_QUEUE_KEY });
        this.tripFinishFired = false;
        this.lastPosition = null;

        // Reset subjects so the next trip's subscriptions don't receive stale values.
        // BehaviorSubjects replay their last value on subscribe — without this reset,
        // a new trip's startTracking() would immediately receive old checkpoint/finish signals.
        this.tripReadyToFinishSub.next(null);
        this.checkpointReachedSub.next(null);
        this.tripCompletedSub.next(false);
    }

    private handleLocationUpdate(location: any) {
        const coords: [number, number] = [location.latitude, location.longitude];
        this.currentLocationSub.next(coords);

        if (!this.tripId) {
            console.warn('[LocationService] GPS update received but NO TRIP ID is set.');
            return;
        }

        const now = Date.now();
        const elapsed = now - this.lastPingTime;
        // Emit on first update, every 10s during movement, or every 25s as heartbeat.
        // The 25s heartbeat fires from this native callback — reliable even when iOS
        // suspends JS timers after the screen locks.
        if (this.lastPingTime === 0 || elapsed >= 10000) {
            const reason = elapsed >= 25000 ? 'Heartbeat' : 'Movement';
            this.emitLocation(coords[0], coords[1], reason);
        }

        if (this.checkpoints.length === 0) return;

        const prev = this.lastPosition;
        this.lastPosition = coords;

        this.checkpoints.forEach(cp => {
            if (this.reachedCheckpoints.has(cp.id)) return;

            // Check direct distance to current position (80m radius)
            const directDist = this.calculateDistance(coords[0], coords[1], cp.lat, cp.lng);
            if (directDist <= 80) { this.markCheckpointReached(cp); return; }

            // Also check the segment from last position to current position.
            // Catches fast-moving buses where the checkpoint falls between two GPS fixes.
            if (prev && this.pointToSegmentDistance(cp.lat, cp.lng, prev[0], prev[1], coords[0], coords[1]) <= 80) {
                this.markCheckpointReached(cp);
            }
        });
    }

    private emitLocation(lat: number, lng: number, reason: string) {
        if (!this.tripId) return;

        this.lastPingTime = Date.now();
        const payload = {
            tripId: Number(this.tripId),
            lineId: Number(this.lineId),
            lat,
            lng,
        };

        if (this._isInForeground && this.socketService.isConnected) {
            console.log(`[Socket] ${reason} emission for trip ${payload.tripId}:`, payload);
            this.socketService.emit('update-location', payload);
        } else {
            const why = !this._isInForeground ? 'background/screen locked' : 'socket offline';
            console.warn(`[Location] ${why} — HTTP push (${reason})`);
            void this.sendViaHttp(payload);
        }
    }

    private async sendViaHttp(payload: { tripId: number; lineId: number; lat: number; lng: number }) {
        const token = this.tokenService.getToken();
        const apiUrl = this.cityService.apiBaseUrl();
        try {
            // CapacitorHttp routes through the native layer — avoids WebView HTTP
            // throttling on Android after 5 min in background (fetch() is throttled).
            const res = await CapacitorHttp.post({
                url: `${apiUrl}/location/push`,
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                data: payload,
            });
            if (res.status < 200 || res.status >= 300) throw new Error(`HTTP ${res.status}`);
            console.log(`[Location] HTTP push OK for trip ${payload.tripId}`);
            // A successful push proves connectivity — drain both queues without waiting for
            // the socket to notice it is back, which can lag by 30-60 s when the screen is locked.
            void this.flushOfflineQueue();
            void this.flushCheckpointQueue();
        } catch (e) {
            console.warn('[Location] HTTP push failed — queuing for retry:', e);
            await this.enqueueLocation(payload);
        }
    }

    /** Persist reached checkpoints so a mid-trip app restart keeps the real visit times. */
    private async persistReachedCheckpoints() {
        const entries = Array.from(this.reachedCheckpoints, ([id, reachedAt]) => ({ id, reachedAt }));
        await Preferences.set({ key: REACHED_CP_KEY, value: JSON.stringify(entries) });
    }

    /** Merge persisted checkpoints back in, keeping whichever time we already hold. */
    private async loadPersistedCheckpoints() {
        const { value } = await Preferences.get({ key: REACHED_CP_KEY });
        if (!value) return;
        try {
            for (const e of JSON.parse(value) as { id: number; reachedAt: string }[]) {
                if (typeof e?.id === 'number' && !this.reachedCheckpoints.has(e.id)) {
                    this.reachedCheckpoints.set(e.id, e.reachedAt);
                }
            }
        } catch { /* corrupt payload — ignore rather than block the trip */ }
    }

    private async enqueueLocation(payload: { tripId: number; lineId: number; lat: number; lng: number }) {
        const entry: QueuedLocation = { ...payload, ts: Date.now() };
        this.offlineQueue.push(entry);
        // Cap queue at 200 points to avoid unbounded storage
        if (this.offlineQueue.length > 200) this.offlineQueue.shift();
        await Preferences.set({ key: OFFLINE_QUEUE_KEY, value: JSON.stringify(this.offlineQueue) });
    }

    private async flushOfflineQueue() {
        // Load persisted queue on first flush (survives app restarts)
        if (this.offlineQueue.length === 0) {
            const { value } = await Preferences.get({ key: OFFLINE_QUEUE_KEY });
            if (value) {
                try { this.offlineQueue = JSON.parse(value); } catch { this.offlineQueue = []; }
            }
        }
        if (this.offlineQueue.length === 0) return;

        const token = this.tokenService.getToken();
        const apiUrl = this.cityService.apiBaseUrl();
        const sent: QueuedLocation[] = [];

        for (const entry of this.offlineQueue) {
            try {
                const res = await CapacitorHttp.post({
                    url: `${apiUrl}/location/push`,
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    data: { tripId: entry.tripId, lineId: entry.lineId, lat: entry.lat, lng: entry.lng },
                });
                if (res.status < 200 || res.status >= 300) break;
                sent.push(entry);
            } catch {
                break;
            }
        }

        if (sent.length > 0) {
            this.offlineQueue = this.offlineQueue.filter(e => !sent.includes(e));
            await Preferences.set({ key: OFFLINE_QUEUE_KEY, value: JSON.stringify(this.offlineQueue) });
            console.log(`[Location] Flushed ${sent.length} queued location(s)`);
        }
    }

    private async markCheckpointReached(cp: Checkpoint) {
        if (!this.tripId) return;

        // Capture the real visit time immediately — before any async work or queuing,
        // so offline flushes still carry the correct on-device timestamp.
        const reachedAt = new Date();

        this.reachedCheckpoints.set(cp.id, reachedAt.toISOString());
        void this.persistReachedCheckpoints();
        this.checkpointReachedSub.next(cp);
        console.log(`[LocationService] Punto de Control alcanzado: ${cp.name} (ID: ${cp.id})`);

        // Trigger finish check immediately instead of waiting for backend sync
        this.checkIfTripFinished();

        // 1. Tactile feedback
        Haptics.impact({ style: ImpactStyle.Heavy });

        // 2. Local Notification
        try {
            await LocalNotifications.schedule({
                notifications: [{
                    id: cp.id,
                    title: '¡Punto de Control alcanzado!',
                    body: `Has llegado a: ${cp.name}`,
                    channelId: 'checkpoints',
                    smallIcon: 'ic_stat_icon_config_sample'
                }]
            });
        } catch (e) {
            console.warn('[LocationService] Notification failed:', e);
        }

        // 3. Backend Sync (CapacitorHttp para evitar throttling en background)
        void this.syncCheckpointReached(this.tripId, cp.id, reachedAt);
    }

    private async syncCheckpointReached(tripId: number, checkpointId: number, reachedAt: Date) {
        const token = this.tokenService.getToken();
        const apiUrl = this.cityService.apiBaseUrl();
        try {
            const res = await CapacitorHttp.post({
                url: `${apiUrl}/trips/${tripId}/checkpoint/${checkpointId}/reach`,
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                data: { visitedAt: reachedAt.toISOString() },
            });
            if (res.status < 200 || res.status >= 300) throw new Error(`HTTP ${res.status}`);
            console.log(`[LocationService] Backend actualizado para el punto ${checkpointId}`);
        } catch (e) {
            // Offline or server unreachable: queue it so the dispatcher sees the checkpoint as
            // soon as signal returns, rather than waiting for /finish to reconcile it at the end
            // of the trip. visitedAt travels with the entry, so the recorded time stays correct.
            console.warn(`[LocationService] Backend sync error for ${checkpointId}, queueing:`, e);
            await this.enqueueCheckpoint({ tripId, checkpointId, visitedAt: reachedAt.toISOString() });
        }
    }

    private async enqueueCheckpoint(entry: QueuedCheckpoint) {
        if (this.checkpointQueue.some(e => e.tripId === entry.tripId && e.checkpointId === entry.checkpointId)) return;
        this.checkpointQueue.push(entry);
        await Preferences.set({ key: CP_QUEUE_KEY, value: JSON.stringify(this.checkpointQueue) });
    }

    private async flushCheckpointQueue() {
        // Load persisted queue on first flush (survives app restarts)
        if (this.checkpointQueue.length === 0) {
            const { value } = await Preferences.get({ key: CP_QUEUE_KEY });
            if (value) {
                try { this.checkpointQueue = JSON.parse(value); } catch { this.checkpointQueue = []; }
            }
        }
        if (this.checkpointQueue.length === 0) return;

        const token = this.tokenService.getToken();
        const apiUrl = this.cityService.apiBaseUrl();
        const sent: QueuedCheckpoint[] = [];

        for (const entry of this.checkpointQueue) {
            try {
                const res = await CapacitorHttp.post({
                    url: `${apiUrl}/trips/${entry.tripId}/checkpoint/${entry.checkpointId}/reach`,
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    data: { visitedAt: entry.visitedAt },
                });
                // 4xx means the server rejected it outright (trip closed, checkpoint gone);
                // retrying forever would pin the queue, so drop it. Only retry on 5xx/network.
                if (res.status >= 500) break;
                sent.push(entry);
            } catch {
                break;
            }
        }

        if (sent.length > 0) {
            this.checkpointQueue = this.checkpointQueue.filter(e => !sent.includes(e));
            await Preferences.set({ key: CP_QUEUE_KEY, value: JSON.stringify(this.checkpointQueue) });
            console.log(`[LocationService] Flushed ${sent.length} queued checkpoint(s)`);
        }
    }

    private async syncArrival(tripId: number, arrivalAt: Date) {
        const token = this.tokenService.getToken();
        const apiUrl = this.cityService.apiBaseUrl();
        try {
            const res = await CapacitorHttp.post({
                url: `${apiUrl}/trips/${tripId}/arrival`,
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                data: { arrivalAt: arrivalAt.toISOString() },
            });
            if (res.status < 200 || res.status >= 300) throw new Error(`HTTP ${res.status}`);
            console.log('[LocationService] arrivalAt sincronizado con el backend.');
        } catch (e) {
            console.error('[LocationService] Error al sincronizar arrivalAt:', e);
        }
    }

    private async checkIfTripFinished() {
        if (!this.tripId || this.tripFinishFired) return;

        const total = this.checkpoints.length;
        const reached = this.reachedCheckpoints.size;

        if (reached >= total && total > 0) {
            this.tripFinishFired = true;
            const now = new Date();
            console.log(`[LocationService] TODOS los puntos de control alcanzados (${reached}/${total}). Preparando cierre con timestamp de llegada.`);

            // Step A: Immediately record arrivalAt in the DB via CapacitorHttp
            // (avoids Android WebView fetch throttling when screen is locked)
            void this.syncArrival(this.tripId, now);

            this.tripReadyToFinishSub.next({ ready: true, arrivalTime: now });
        }
    }

    public finishTripAndNotify(passengerCount?: number, explicitTripId?: number, arrivalTime?: Date): Observable<any> {
        const id = explicitTripId || this.tripId;

        if (!id) {
            console.error('[LocationService] finishTripAndNotify ERROR: No Trip ID found in state OR parameters.');
            throw new Error('No hay un viaje activo para finalizar.');
        }

        console.log('[LocationService] Finalizing Trip ID:', id);

        // 1. Final Notification (Non-blocking)
        try {
            LocalNotifications.schedule({
                notifications: [{
                    id: 999,
                    title: '¡Viaje Finalizado!',
                    body: 'Has completado todos los puntos de control con éxito. Buen trabajo.',
                    channelId: 'checkpoints',
                    smallIcon: 'ic_stat_icon_config_sample'
                }]
            });
        } catch (e) {
            console.warn('[LocationService] Final notification failed:', e);
        }

        // 2. Mark as Completed in backend — send each locally-tracked checkpoint with the time
        //    it was actually passed, so the backend can reconcile any that failed to sync
        //    mid-trip without collapsing them onto the finish timestamp.
        const reached = Array.from(this.reachedCheckpoints, ([cpId, reachedAt]) => ({ id: cpId, reachedAt }));
        return this.tripsService.finishTrip(id, passengerCount, arrivalTime, reached).pipe(
            tap(() => {
                console.log(`[LocationService] Trip ${id} finalized successfully on backend. Cleaning up local state.`);
                this.stopTracking();
            })
        );
    }

    /**
     * Minimum distance in meters from point P to line segment A→B.
     * Uses a flat-earth approximation valid for city-scale distances (<5 km).
     */
    private pointToSegmentDistance(
        pLat: number, pLng: number,
        aLat: number, aLng: number,
        bLat: number, bLng: number,
    ): number {
        const cosLat = Math.cos(((aLat + bLat + pLat) / 3) * Math.PI / 180);
        const mPerDegLat = 111_320;
        const mPerDegLng = 111_320 * cosLat;

        const px = pLng * mPerDegLng, py = pLat * mPerDegLat;
        const ax = aLng * mPerDegLng, ay = aLat * mPerDegLat;
        const bx = bLng * mPerDegLng, by = bLat * mPerDegLat;

        const dx = bx - ax, dy = by - ay;
        const lenSq = dx * dx + dy * dy;
        if (lenSq === 0) return Math.hypot(px - ax, py - ay);

        const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
        return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
    }

    /**
     * Haversine distance in meters
     */
    private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6371000; // Earth radius in meters
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }
}
